/* ============================================================
   Landing Slider
   Lightweight, dependency-free scroll-snap carousel used by the
   landing page slider sections. Native touch/trackpad swipe via
   overflow scrolling; arrows and dots are wired up here.

   Dots represent REACHABLE scroll positions, not raw slide count:
   with N slides and P visible per view there are (N - P + 1) stops,
   so there are never dead dots and "next" stays enabled until the
   true last position.

   Markup contract (per slider):
     [data-landing-slider]
       [data-slider-track]      -> the horizontal scroller
         > children             -> the slides
       [data-slider-prev]       -> previous button (optional)
       [data-slider-next]       -> next button (optional)
       [data-slider-dots]       -> empty container; dots injected
============================================================ */
(function () {
  function initSlider(root) {
    var track = root.querySelector('[data-slider-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    if (slides.length === 0) return;

    var prevBtn = root.querySelector('[data-slider-prev]');
    var nextBtn = root.querySelector('[data-slider-next]');
    var dotsWrap = root.querySelector('[data-slider-dots]');
    var dots = [];

    function metrics() {
      var first = slides[0];
      var style = getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap) || 0;
      var step = first.getBoundingClientRect().width + gap;
      var perView = step > 0 ? Math.max(1, Math.round((track.clientWidth + gap) / step)) : 1;
      return { step: step, perView: perView };
    }

    function maxIndex() {
      return Math.max(0, slides.length - metrics().perView);
    }

    function currentIndex() {
      var step = metrics().step;
      if (step === 0) return 0;
      var maxLeft = track.scrollWidth - track.clientWidth;
      if (track.scrollLeft >= maxLeft - 1) return maxIndex();
      return Math.min(maxIndex(), Math.round(track.scrollLeft / step));
    }

    function goTo(index) {
      index = Math.max(0, Math.min(maxIndex(), index));
      track.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
    }

    function buildDots() {
      if (!dotsWrap) return;
      dots = [];
      dotsWrap.innerHTML = '';
      var total = maxIndex() + 1; // one dot per reachable step
      for (var i = 0; i < total; i++) {
        (function (i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'landing-slider__dot';
          dot.setAttribute('aria-label', 'Go to position ' + (i + 1));
          dot.addEventListener('click', function () { goTo(i); });
          dotsWrap.appendChild(dot);
          dots.push(dot);
        })(i);
      }
    }

    function update() {
      var index = currentIndex();

      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (prevBtn) {
        prevBtn.disabled = index <= 0;
      }
      if (nextBtn) {
        nextBtn.disabled = index >= maxIndex();
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(currentIndex() - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(currentIndex() + 1);
      });
    }

    // Throttle scroll updates with requestAnimationFrame
    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', function () {
      buildDots();
      update();
    });

    buildDots();
    update();
  }

  function initAll() {
    document.querySelectorAll('[data-landing-slider]').forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when a section is added/edited in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-landing-slider]').forEach(initSlider);
  });
})();
